import { addDays } from 'date-fns/addDays';
import { startOfDay } from 'date-fns/startOfDay';

/**
 * UI-agnostic calendar time primitives.
 *
 * These are pure date/number utilities shared by any calendar presentation
 * layer. They carry no rendering, layout or pixel semantics — only the
 * underlying temporal math that must stay stable across UI redesigns.
 */

/** Returns `count` consecutive days starting at `start` (inclusive). */
export function makeDays(start: Date, count: number): Date[] {
  return Array.from({ length: count }, (_, index) => addDays(start, index));
}

/** Minutes elapsed since the local start of `value`'s day (0..1439). */
export function minutesSinceStartOfDay(value: Date): number {
  return value.getHours() * 60 + value.getMinutes();
}

/**
 * Local wall-clock time `minute` minutes after the start of `day`. Minutes
 * below 0 or from 1440 on carry over into the neighbouring days, so a moved
 * start crosses midnight instead of being clipped to `day`.
 */
export function dateAtDayMinute(day: Date, minute: number): Date {
  const dayOffset = Math.floor(minute / (24 * 60));
  const minuteOfDay = minute - dayOffset * 24 * 60;
  const date = addDays(startOfDay(day), dayOffset);
  date.setHours(Math.floor(minuteOfDay / 60), minuteOfDay % 60);
  return date;
}

/**
 * Whole minutes between `start` and `end`, rounded to nearest minute and
 * clamped at 0 (never negative).
 */
export function minutesBetween(start: Date, end: Date): number {
  return Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
}
